import { CashSpecialIcon, AddCardIcon } from "lola-framework-ui-test";

import { PayoutInfo } from "./PayoutInfo";
import { useStore } from "../../../../store/globalState";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES, flavorClassName } from "../../../../constants";
import { paymentType } from "../../../../interfaces/payment";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "../../../../store/useSessionStore";
import { getAllPaymentMethods, isDeadLinkError } from "../../../../services/api";
import { getEvents, safeCapture } from "../../../../analytics";
import type { ICardPaymentMethod } from "../../../../interfaces/payment-method";
import masterCardLogo from "../../../../assets/master_card_icon.svg";
import visaLogo from "../../../../assets/visa_color_icon.svg";
import { useRouteWithToken } from "../../../../hooks/useRouteWithToken";
import { useTransferPricing } from "../../../../hooks/useTransferPricing";
import { resolvePickerRowFee } from "../../../../utils/resolvePickerRowFee";

export interface SelectPaymentMethodSlotProps {
  onClose: () => void;
  /** @deprecated Fees are now derived per-method from the pre-quote. */
  fee?: number;
}

export const SelectPaymentMethodSlot: React.FC<
  SelectPaymentMethodSlotProps
> = ({ onClose }) => {
  const { t } = useTranslation();
  const { theme, setPaymentState, paymentState, cardList, exchangeRate } = useStore();
  const { session } = useSessionStore();
  const colorConfig = theme.whiteView;
  const { footerColor } = colorConfig;
  const navigate = useNavigate();
  const { redirectTo } = useRouteWithToken();

  // Availability + per-method economics come from the pre-quote (scoped to the
  // claim's fixed payout). Only funding methods that can actually fulfil the
  // chosen payout are offered.
  const { availableCategories, optionForCategory, quote, selectedOption } =
    useTransferPricing();
  const showCash = availableCategories.has("CASH");
  const showCards = availableCategories.has("CARD");
  const cashFee = optionForCategory("CASH")?.customerFee;
  const cardFee = optionForCategory("CARD")?.customerFee;
  const hasQuote = Boolean(quote);
  const quoteFee = quote ? (quote.totalFee ?? quote.fee ?? 0) : null;
  const composedOptionFee = selectedOption?.customerFee ?? null;
  const rowFee = (
    isHighlight: boolean,
    methodEstimateFee: number | undefined
  ) =>
    resolvePickerRowFee({
      isHighlight,
      hasQuote,
      quoteFee,
      composedOptionFee,
      methodEstimateFee: methodEstimateFee ?? null,
    });

  const [cards, setCards] = useState<ICardPaymentMethod[]>([]);

  useEffect(() => {
    const linkId = session.data?.linkId;
    if (!linkId) return;
    if (!showCards) {
      setCards([]);
      return;
    }

    getAllPaymentMethods(linkId)
      .then((methods) => {
        setCards(methods.cards.filter((c) => c.enabled));
      })
      .catch((err) => {
        console.error("Failed to fetch payment methods:", err);
        if (isDeadLinkError(err)) {
          return;
        }
        navigate(APP_ROUTES.SERVICE_UNAVAILABLE_PAGE, { replace: true });
      });
  }, [session.data?.linkId, cardList, showCards]);

  const onSelectPayment = ({
    cardId,
    type,
    brand,
  }: {
    cardId: string | null;
    type: paymentType;
    brand: string;
  }) => {
    safeCapture(getEvents().FUNDING_SELECTED, { funding_method: type });
    setPaymentState({
      payment: {
        cardId,
        type,
        brand,
      },
    });
    onClose();
  };

  const pickIcon = useCallback((brand: string) => {
    let finalIcon = null;
    if (brand.toLocaleUpperCase().includes("VISA")) finalIcon = visaLogo;
    if (brand.toLocaleUpperCase().includes("MASTERCARD"))
      finalIcon = masterCardLogo;

    return {
      brand,
      icon: <img src={finalIcon ?? ""} alt={`${brand} logo`} />,
    };
  }, []);

  return (
    <section
      className={`select-payer-slot white-view-background ${flavorClassName}`}
    >
      <h2 className="foreground text-centered h2">
        {t("payment.selectMethod")}
      </h2>
      <section className="select-payer-slot-container">
        {showCash && (
          <PayoutInfo
            title={t("payment.cashDeposit")}
            subtitle={t("payment.minMax", {
              min: exchangeRate?.lowerLimit ?? "0",
              max: exchangeRate?.upperLimit ?? "0",
            })}
            icon={
              <CashSpecialIcon
                size={32}
                className="flavor-c-icon"
                colors={[footerColor, footerColor]}
              />
            }
            fee={
              <label className="muted-foreground">
                {t("payment.fee", {
                  amount:
                    rowFee(
                      paymentState.payment?.type === paymentType.CASH,
                      cashFee
                    ) ?? "0",
                })}
              </label>
            }
            onChange={() =>
              onSelectPayment({
                cardId: null,
                type: paymentType.CASH,
                brand: t("payment.cashDeposit"),
              })
            }
          />
        )}
        {showCash && showCards && <span className="divisor" />}
        {showCards && (
          <>
            {cards.map((card) => {
              const displayName = `${card.brand}-${card.last4}`;
              return (
                <PayoutInfo
                  key={card.paymentMethodId}
                  title={displayName}
                  subtitle={t("payment.minMax", {
                    min: exchangeRate?.lowerLimit ?? "0",
                    max: exchangeRate?.upperLimit ?? "0",
                  })}
                  icon={pickIcon(card.brand).icon}
                  fee={
                    <label className="muted-foreground">
                      {t("payment.fee", {
                        amount:
                          rowFee(
                            paymentState.payment?.type === paymentType.CARD &&
                              paymentState.payment?.cardId ===
                                card.paymentMethodId,
                            cardFee
                          ) ?? "0",
                      })}
                    </label>
                  }
                  onChange={() =>
                    onSelectPayment({
                      cardId: card.paymentMethodId,
                      type: paymentType.CARD,
                      brand: displayName,
                    })
                  }
                />
              );
            })}
            <span className="divisor" />
            <PayoutInfo
              title={t("payment.addDebitCard")}
              subtitle={t("payment.minMax", {
                min: exchangeRate?.lowerLimit ?? "0",
                max: exchangeRate?.upperLimit ?? "0",
              })}
              icon={
                <AddCardIcon
                  size={32}
                  className="flavor-c-icon"
                  colors={[footerColor, footerColor]}
                />
              }
              fee={
                <label className="muted-foreground">
                  {t("payment.fee", { amount: rowFee(false, cardFee) ?? "0" })}
                </label>
              }
              onChange={() => redirectTo(APP_ROUTES.ADD_CARD_PAGE)}
            />
          </>
        )}
      </section>
    </section>
  );
};
